import React from 'react';
import block from 'bem-cn-lite';
import {HTML} from '@doc-tools/components';
import {compose} from 'recompose';
import {ClassNameProps} from '@yandex-data-ui/cloud-components';

import {
    HeaderBlockBackground,
    HeaderBlockProps,
    headerHasMediaBackground,
    HeaderImageSize,
} from 'units/constructor-local/models';
import {
    HeaderWidth,
    Button,
    Media,
    BackgroundMedia,
    BackgroundImage,
    Grid,
    Row,
    Col,
} from '@yandex-data-ui/page-constructor';
import {PreviewButton} from 'units/constructor-local/components/';
import {PREVIEW_STATUSES} from 'units/constructor-local/utils';
import {PageContextProps} from 'units/constructor-local/contexts/PageContext';
import {SOLUTIONS_URL} from 'units/solutions/constants';

import withPageContext from 'units/constructor-local/hoc/withPageContext';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import YFMWrapper from 'components/YFMWrapper/YFMWrapper';
import BackLink from 'components/BackLink/BackLink';
import FullWidthBackground from 'components/FullWidthBackground/FullWidthBackground';
import HeaderBreadcrumbs from 'units/constructor-local/components/HeaderBreadcrumbs/HeaderBreadcrumbs';

import './Header.scss';

const b = block('HeaderBlock');

type HeaderBlockFullProps = HeaderBlockProps &
    PageContextProps &
    WithTranslationProps &
    ClassNameProps;

function titleWithImageSizes(imageSize: HeaderImageSize) {
    switch (imageSize) {
        case 's':
            return {
                md: 8,
                all: 12,
            };
        case 'm':
            return {
                md: 6,
                all: 12,
            };
        default:
            return {all: 12};
    }
}

function getTitleSizes(width: HeaderWidth) {
    switch (width) {
        case 's':
            return {
                lg: 6,
                sm: 12,
                md: 6,
                all: 12,
            };
        case 'm':
            return {
                lg: 8,
                md: 8,
                sm: 12,
                all: 12,
            };
        default:
            return {all: 12};
    }
}

function renderBackground(background: HeaderBlockBackground) {
    return headerHasMediaBackground(background) ? (
        <BackgroundMedia {...background} className={b('background-media')} />
    ) : (
        <BackgroundImage
            src={background.url}
            className={b('background')}
            imageClassName={b('background-img')}
            style={{backgroundColor: background.fullWidth ? '' : background?.color}}
            disableCompress={background?.disableCompress}
        />
    );
}

function renderFullWidthBackground(background: HeaderBlockBackground) {
    return !headerHasMediaBackground(background) && Boolean(background?.fullWidth) ? (
        <FullWidthBackground style={{backgroundColor: background?.color}} theme="rounded" />
    ) : null;
}

// eslint-disable-next-line complexity
const HeaderBlock: React.FunctionComponent<HeaderBlockFullProps> = (props) => {
    const {
        title,
        overtitle,
        description,
        buttons,
        service,
        image,
        video,
        solution,
        backLink,
        breadcrumbs,
        i18nK,
        width = 'l',
        offset = 'default',
        imageSize = 'm',
        background,
        theme = 'default',
        verticalOffset,
        className,
    } = props;
    const hasPrimaryButton = buttons?.find((button) => button.primary);
    const hasMedia = Boolean(image || video);
    const titleSizes = hasMedia ? titleWithImageSizes(imageSize) : getTitleSizes(width);
    const link =
        backLink ||
        (solution && {url: SOLUTIONS_URL, title: i18nK('constructor_page-solutions-back-button')});
    let curVerticalOffset = verticalOffset;

    if (hasMedia && !verticalOffset) {
        curVerticalOffset = 'm';
    }

    return (
        <header
            className={b(
                {['has-media']: hasMedia, ['has-background']: Boolean(background)},
                className,
            )}
        >
            {link && <BackLink {...link} className={b('back-link')} />}
            {breadcrumbs && (
                <HeaderBreadcrumbs {...breadcrumbs} className={b('breadcrumbs')} theme={theme} />
            )}
            {background && renderFullWidthBackground(background)}
            <Grid className={b('content', {offset, theme, 'vertical-offset': curVerticalOffset})}>
                {background && renderBackground(background)}
                <Row className={b('content-outer')}>
                    <Col sizes={titleSizes} className={b('content-inner')}>
                        {overtitle && (
                            <h4 className={b('overtitle')}>
                                <HTML>{overtitle}</HTML>
                            </h4>
                        )}
                        <h1 className={b('title')}>
                            {service && PREVIEW_STATUSES.includes(service.status) && (
                                <div className={b('preview')}>Preview</div>
                            )}
                            <HTML>{title}</HTML>
                        </h1>
                        {description && (
                            <h5 className={b('description')}>
                                <YFMWrapper
                                    content={description}
                                    modifiers={{
                                        constructorHeaderDescription: true,
                                    }}
                                />
                            </h5>
                        )}
                        {(service || buttons) && (
                            <div className={b('buttons')} data-qa="header-buttons">
                                {!hasPrimaryButton && service && (
                                    <PreviewButton className={b('button')} />
                                )}
                                {buttons &&
                                    buttons.map((button, index) => (
                                        <Button
                                            key={index}
                                            className={b('button')}
                                            size="xl"
                                            {...button}
                                        />
                                    ))}
                            </div>
                        )}
                    </Col>
                </Row>
                {hasMedia && (
                    <Media
                        className={b('media', {[imageSize]: true})}
                        videoClassName={b('video')}
                        imageClassName={b('image')}
                        video={video}
                        image={image}
                    />
                )}
            </Grid>
        </header>
    );
};
export default compose<HeaderBlockFullProps, HeaderBlockProps & ClassNameProps>(
    withPageContext,
    withTranslation('constructor'),
)(HeaderBlock);
